
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { Check, Sparkles, Zap } from 'lucide-react';
import { Button } from '../ui/Button';
import { TIER_CONFIGS, Tier } from '../../types/tiers';
import { motion, AnimatePresence } from 'framer-motion';
import { PlanCheckoutModal } from './PlanCheckoutModal';
import { fetchTokenPrice } from '../../services/prices';

interface PricingProps {
    onGetStarted: () => void;
}

export const Pricing: React.FC<PricingProps> = ({ onGetStarted }) => {
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
    const [selectedPlan, setSelectedPlan] = useState<{ tier: Tier, price: number } | null>(null);
    const [solPrice, setSolPrice] = useState<number | null>(null);

    useEffect(() => {
        const getSolPrice = async () => {
            const price = await fetchTokenPrice('SOL');
            if (price) {
                setSolPrice(price);
            }
        };
        getSolPrice();
    }, []);

    const getDisplayPrice = (tier: Tier): number => {
        const plan = TIER_CONFIGS[tier];
        if (billingCycle === 'yearly') {
            return Math.round(plan.price.yearly / 12);
        }
        return plan.price.monthly;
    };

    const renderPrice = (tier: Tier) => {
        const price = getDisplayPrice(tier);
        if (price === 0) {
            return '$0';
        }
        return `$${price}`;
    };

    const getPlanPriceValue = (tier: Tier) => {
        const plan = TIER_CONFIGS[tier];
        if (billingCycle === 'yearly') return plan.price.yearly;
        return plan.price.monthly;
    };

    const getCycleLabel = () => {
        if (billingCycle === 'yearly') return '/ mo (billed yearly)';
        return '/ month';
    };

    const handlePlanSelect = (tier: Tier) => {
        const price = getPlanPriceValue(tier);
        if (price > 0) {
            setSelectedPlan({ tier, price });
        } else {
            onGetStarted();
        }
    };

    const handlePurchaseSuccess = () => {
        setSelectedPlan(null);
        onGetStarted();
    };

    // Visible tiers: Merchant, Enterprise, Premium. Lifetime is hidden.
    const tiers: Tier[] = ['Merchant', 'Enterprise', 'Premium'];

    return (
        <section id="pricing" className="py-24 md:py-32 bg-stone-50 dark:bg-[#0A0B0F] relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-gradient-to-b from-white/0 via-nobel-gold/5 to-white/0 pointer-events-none"></div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
                    <h2 className="font-serif text-3xl md:text-5xl text-[#0A0B0F] dark:text-white mb-6 leading-tight px-4">
                        Sovereign Infrastructure. <br/> <span className="italic text-stone-500 dark:text-stone-400">Transparent Pricing.</span>
                    </h2>
                    <p className="text-stone-600 dark:text-stone-400 text-base md:text-lg px-6">
                        Professional grade financial tools. Choose the stack that fits your volume.
                    </p>
                </div>

                {/* Billing Toggle - Radius set to SM (2px) */}
                <div className="flex flex-col items-center justify-center gap-4 mb-16 md:mb-28 relative z-20">
                    <div className="relative">
                        <div className="bg-white dark:bg-[#1C1E26] p-1 md:p-1.5 rounded-sm border border-stone-200 dark:border-white/10 flex gap-1 shadow-smooth relative z-10">
                            {(['monthly', 'yearly'] as const).map((cycle) => (
                                <button
                                    key={cycle}
                                    onClick={() => setBillingCycle(cycle)}
                                    className={`relative px-6 sm:px-10 py-2.5 sm:py-3 rounded-sm text-xs sm:text-sm font-black uppercase tracking-widest transition-all duration-300 ${
                                        billingCycle === cycle 
                                        ? 'text-white shadow-lg' 
                                        : 'text-stone-500 hover:text-[#0A0B0F] dark:hover:text-white'
                                    }`}
                                >
                                    <span className="relative z-10">{cycle}</span>
                                    {billingCycle === cycle && (
                                        <motion.div 
                                            layoutId="billing-highlight"
                                            className="absolute inset-0 bg-[#0A0B0F] rounded-sm"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                        {/* Floating Badge */}
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-max z-20">
                             <div className="bg-nobel-gold text-[#0A0B0F] text-[9px] sm:text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wide shadow-lg flex items-center gap-1.5 transform hover:scale-105 transition-transform cursor-default border-2 border-white dark:border-[#0A0B0F]">
                                <Zap size={10} fill="currentColor" /> Save 20% <span className="hidden sm:inline">on Yearly</span>
                             </div>
                             <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-nobel-gold absolute left-1/2 -translate-x-1/2 -bottom-1.5"></div>
                        </div>
                    </div>
                    {solPrice && (
                        <p className="text-[10px] sm:text-xs text-stone-500 dark:text-stone-400 font-mono font-bold">
                            MARKET: 1 SOL ≈ ${solPrice.toFixed(2)}
                        </p>
                    )}
                </div>

                {/* Pricing Grid - Cards updated to rounded-sm (2px) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch max-w-6xl mx-auto px-4 sm:px-0">
                    {tiers.map((tierName) => {
                        const tier = TIER_CONFIGS[tierName];
                        const isPopular = tier.highlight;
                        const isActiveStyle = isPopular;

                        const displayedPrice = getDisplayPrice(tierName);
                        const solAmount = displayedPrice > 0 && solPrice ? (displayedPrice / solPrice).toFixed(2) : null;
                        
                        return (
                            <div 
                                key={tierName} 
                                className={`relative flex flex-col p-8 sm:p-10 rounded-sm border transition-all duration-500 group h-full ${
                                    isActiveStyle
                                    ? 'bg-[#0A0B0F] border-[#0A0B0F] text-white shadow-[0_40px_80px_-15px_rgba(0,0,0,0.5)] md:scale-105 z-10' 
                                    : 'bg-white dark:bg-[#1C1E26] border-stone-200 dark:border-white/10 text-[#0A0B0F] dark:text-white hover:border-nobel-gold/50 dark:hover:border-nobel-gold/30 hover:shadow-xl'
                                }`}
                            >
                                {isPopular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-nobel-gold to-[#A08040] text-white text-[9px] sm:text-[10px] font-bold px-5 py-2 rounded-full uppercase tracking-widest shadow-lg flex items-center gap-1.5 w-max">
                                        <Sparkles size={12} fill="currentColor"/> Preferred for Scale
                                    </div>
                                )}

                                <div className="mb-6">
                                    <h3 className="font-serif text-2xl sm:text-3xl font-bold mb-3">{tier.name}</h3>
                                    <p className={`text-xs leading-relaxed min-h-[48px] ${isActiveStyle ? 'text-stone-400' : 'text-stone-500'}`}>
                                        {tier.description}
                                    </p>
                                </div>

                                <div className="mb-8 pt-6 border-t border-dashed border-stone-200 dark:border-white/10 flex justify-between items-end">
                                    <div>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl sm:text-5xl font-serif font-bold tracking-tighter leading-none">
                                                {renderPrice(tierName)}
                                            </span>
                                        </div>
                                        <div className={`text-[10px] font-black uppercase tracking-wider mt-3 ${isActiveStyle ? 'text-stone-500' : 'text-stone-400'}`}>
                                            {getCycleLabel()}
                                        </div>
                                        {solAmount ? (
                                            <div className={`text-[11px] mt-2 font-mono font-bold h-4 ${isActiveStyle ? 'text-stone-400' : 'text-stone-500'}`}>
                                                ≈ {solAmount} SOL
                                            </div>
                                        ) : (
                                            <div className="h-4 mt-2"></div>
                                        )}
                                    </div>
                                    
                                    <div className={`mb-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-[10px] font-black tracking-widest border ${
                                        isActiveStyle
                                        ? 'bg-white/10 border-white/10 text-nobel-gold' 
                                        : 'bg-stone-50 dark:bg-white/5 border-stone-100 dark:border-white/10 text-stone-600 dark:text-stone-300'
                                    }`}>
                                        FEE: {(tier.swapFee * 100).toFixed(1)}%
                                    </div>
                                </div>

                                <ul className="space-y-4 mb-10 flex-1">
                                    {tier.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <div className={`mt-1 w-4 h-4 rounded-sm flex items-center justify-center shrink-0 shadow-sm ${
                                                isActiveStyle
                                                ? 'bg-nobel-gold text-black' 
                                                : 'bg-stone-900 dark:bg-white text-white dark:text-black'
                                            }`}>
                                                <Check size={10} strokeWidth={4} />
                                            </div>
                                            <span className={`text-[13px] font-medium leading-tight ${isActiveStyle ? 'text-stone-300' : 'text-stone-600 dark:text-stone-400'}`}>
                                                {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto">
                                    <Button
                                        onClick={() => handlePlanSelect(tierName)}
                                        variant={isActiveStyle ? 'secondary' : 'outline'}
                                        className={`w-full py-4 text-xs font-black uppercase tracking-[0.2em] rounded-sm transition-all duration-300 ${isActiveStyle ? 'bg-white !text-black hover:bg-stone-200' : ''}`}
                                    >
                                        {tier.price.monthly === 0 ? 'Initialize Free Node' : 'Initialize Protocol'}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <AnimatePresence>
                {selectedPlan && (
                    <PlanCheckoutModal 
                        tier={selectedPlan.tier}
                        cycle={billingCycle}
                        price={selectedPlan.price}
                        onClose={() => setSelectedPlan(null)}
                        onSuccess={handlePurchaseSuccess}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};
